/*
  «Дела по счёту» над шапкой карточки - вариант В2, выбор владельца 06.10.2026.

  Строка на дело: кто вёл, месяцы, где сейчас, сумма. Последняя строка - месяцы с
  долгом, не вошедшие ни в одно дело. Период нажимается и открывает окно с
  месяцами и долгом каждого - как свёрнутый перечень варианта В3 (условие
  владельца при выборе). У месяца названы все дела, в которые он входит: месяц в
  двух делах - возможная вторая подача.

  Всё, что написано в строках, приходит с сервера (`/accounts/{id}/cases`):
  клиент только раскладывает. Кнопки строк (указание владельца 06.10.2026) ведут
  к блоку вкладок внизу и открывают нужную (`onGo` страницы): «Предъявить» -
  на «Суд» к сборке пакета, «Отметить: ...» у нашего дела - на «Суд» к смене
  стадии с выбранной следующей.
*/

import { useState } from 'react'

import { money, monthTitle } from '../api/format.js'
import { Tag } from './Common.jsx'
import Modal from './Modal.jsx'
import PageBar from './PageBar.jsx'
import usePaging from './usePaging.js'

function PeriodButton({ title, months, onOpen }) {
  if (!title) return <span className="muted">месяцы не отмечены</span>
  return (
    <button type="button" className="link-button" onClick={() => onOpen({ title, months })}>
      {title}
    </button>
  )
}

function MonthsWindow({ shown, lines, onClose }) {
  const wanted = new Set(shown.months)
  // Свежие месяцы сверху - как в перечне варианта В3: смотрят обычно последние.
  const all = lines.filter((line) => wanted.has(line.period)).reverse()
  // Дело за пять лет - шестьдесят месяцев в одном окне; с 21.10.2026 - общая панель страниц.
  const { rows, bar } = usePaging(all)
  return (
    <Modal title={`Месяцы: ${shown.title}`} onClose={onClose}>
      <div className="table-scroll case-months__window">
        <table className="table">
          <thead>
            <tr>
              <th>Месяц</th>
              <th className="table__num">Долг</th>
              <th>Где</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((line) => (
              <tr key={line.period}>
                <td>{monthTitle(line.period)}</td>
                <td className="table__num" data-label="Долг">
                  {money(line.debt)}
                </td>
                <td className="table__block" data-label="Где">
                  {line.places.join('; ')}
                  {line.places.length > 1 ? (
                    <div className="onec__skipped">месяц входит в несколько дел - проверьте</div>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <PageBar {...bar} what="месяцев" />
      <div className="row row--tight">
        <button className="button button--quiet" type="button" onClick={onClose}>
          Закрыть
        </button>
      </div>
    </Modal>
  )
}

/*
  Действие строки (указание владельца 06.10.2026): у дела УК - отметить месяцы,
  у нашего дела - следующий шаг. Кнопка шага сама стадию не меняет, а ведёт
  туда, где шаг делается:
  - «приказ получен» - на вкладку «Приказ», к форме «Приказ получен»: заведённый
    приказ сам переводит счёт в эту стадию (замечание владельца 06.10.2026 -
    первая версия вела на «Суд», а ждут приказ там, где его заводят);
  - остальное - на «Суд», к смене стадии с выбранной следующей: там человек
    пишет причину, без неё смена в аудит не уходит.
*/
const STEP_TAB = { order_received: 'order' }

function goToStep(onGo, stage) {
  const tab = STEP_TAB[stage] ?? 'court'
  onGo(tab, tab === 'court' ? stage : null)
}
/*
  «Суд вернул заявление» (решение владельца 26.10.2026) - рядом со следующим
  шагом нашего дела в суде. Окно спросит дату определения и причину; в режиме
  «только чтение» кнопка видна, но выключена - как прочие действия карточки.
*/
function ReturnAction({ row, readonly, onReturn }) {
  if (!row.can_return || !onReturn) return null
  return (
    <button
      className="button button--quiet"
      type="button"
      disabled={Boolean(readonly)}
      onClick={() => onReturn(row)}
    >
      Суд вернул заявление
    </button>
  )
}

function RowAction({ row, onEdit, onGo, readonly, onReturn }) {
  if (row.can_set_months) {
    return (
      <button className="button button--quiet" type="button" onClick={() => onEdit(row)}>
        {row.period ? 'Изменить месяцы' : 'Отметить месяцы'}
      </button>
    )
  }
  const step =
    row.next_stage && onGo ? (
      <button className="button button--quiet" type="button" onClick={() => goToStep(onGo, row.next_stage)}>
        Отметить: {row.next_title}
      </button>
    ) : null
  return (
    <div className="row row--tight">
      {step}
      <ReturnAction row={row} readonly={readonly} onReturn={onReturn} />
    </div>
  )
}

function CaseRow({ row, canAct, onOpen, onEdit, onGo, readonly, onReturn }) {
  return (
    <tr>
      <td>
        {row.who}
        <div className="muted">{row.who_note}</div>
      </td>
      <td data-label="Месяцы">
        <PeriodButton title={row.period} months={row.months} onOpen={onOpen} />
      </td>
      <td className="table__block" data-label="Где сейчас">
        <Tag kind={row.tone}>{row.where}</Tag>
        {row.where_note ? <div className="muted">{row.where_note}</div> : null}
      </td>
      <td className="table__num" data-label="Сумма">
        {row.amount == null ? <span className="muted">-</span> : money(row.amount)}
      </td>
      <td data-label="">
        {canAct ? (
          <RowAction row={row} onEdit={onEdit} onGo={onGo} readonly={readonly} onReturn={onReturn} />
        ) : null}
      </td>
    </tr>
  )
}

/*
  «Предъявить» - только где счёт можно отметить поданным (`can_claim` с
  сервера). Стадия одна на счёт (решение владельца 26.10.2026, строка 205):
  новый долг поданного счёта взыскивается следующим заявлением после завершения
  текущего дела, и пояснение об этом стоит в «Где сейчас» - его видят все.
*/
function ClaimAction({ free, canAct, onGo }) {
  if (!canAct || !free.can_claim) return null
  return (
    <button className="button" type="button" onClick={() => onGo?.('court')}>
      Предъявить
    </button>
  )
}

export default function AccountCases({ table, canAct, onEdit, onGo, readonly, onReturn }) {
  const [shown, setShown] = useState(null)
  // Дел по счёту обычно несколько, но панель сама покажет лишь «Всего дел: N» (21.10.2026).
  // Строка «Не предъявлено» - итог, а не запись: она стоит под любой страницей.
  const { rows, bar } = usePaging(table ? table.rows : null)
  if (!table || !table.rows.length) return null
  const free = table.unclaimed
  return (
    <section className="section card">
      <h2 className="section__title">Дела по счёту</h2>
      <div className="table-scroll">
        <table className="table table--account-cases">
          <thead>
            <tr>
              <th>Кто</th>
              <th>Месяцы</th>
              <th>Где сейчас</th>
              <th className="table__num">Сумма</th>
              <th aria-label="Действие" />
            </tr>
          </thead>
          <tbody>
            {rows.map((row) => (
              <CaseRow
                key={row.id}
                row={row}
                canAct={canAct}
                onOpen={setShown}
                onEdit={onEdit}
                onGo={onGo}
                readonly={readonly}
                onReturn={onReturn}
              />
            ))}
            {free ? (
              <tr className="account-cases__free">
                <td>
                  <b>Не предъявлено</b>
                </td>
                <td data-label="Месяцы">
                  <PeriodButton title={free.period} months={free.months} onOpen={setShown} />
                </td>
                <td className="table__block" data-label="Где сейчас">
                  <Tag kind="calm">ещё не в суде</Tag>
                  <div className="muted">
                    {free.can_claim
                      ? 'войдут в заявление'
                      : 'взыщем следующим заявлением, когда завершится текущее дело'}
                  </div>
                </td>
                <td className="table__num" data-label="Сумма">
                  <b>{money(free.amount)}</b>
                </td>
                <td data-label="">
                  <ClaimAction free={free} canAct={canAct} onGo={onGo} />
                </td>
              </tr>
            ) : null}
          </tbody>
        </table>
      </div>
      <PageBar {...bar} what="дел" />
      {shown ? (
        <MonthsWindow shown={shown} lines={table.months} onClose={() => setShown(null)} />
      ) : null}
    </section>
  )
}
